import { useMemo, useState } from 'react';
import { useChannelStateContext } from 'stream-chat-react';
import Avatar from '@mui/material/Avatar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import GGCustomMessage from '../../../components/PageComponents/ChatComponents/GGCustomMessage';
import './MonitorChannelView.scss';

type Role = 'Buyer' | 'Seller' | 'Admin' | 'Unknown';

const ROLE_COLORS: Record<Role, string> = {
  Buyer: '#2563eb',
  Seller: '#059669',
  Admin: '#6b7280',
  Unknown: '#9ca3af',
};

const formatTime = (date?: string | Date): string => {
  if (!date) return '';
  try {
    return new Date(date).toLocaleString();
  } catch {
    return '';
  }
};

// channelId format: {buyerId}_{gigId}
const parseChannelId = (channelId?: string): { buyerId: string; gigId: string } => {
  if (!channelId) return { buyerId: '', gigId: '' };
  const idx = channelId.indexOf('_');
  if (idx === -1) return { buyerId: channelId, gigId: '' };
  return { buyerId: channelId.slice(0, idx), gigId: channelId.slice(idx + 1) };
};

const IdChip = ({ label, value }: { label?: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <button className="monitor-idchip" onClick={copy} title={`Copy ${label ?? 'id'}`}>
      {label && <span className="monitor-idchip-label">{label}</span>}
      <code className="monitor-idchip-value">{value || '\u2014'}</code>
      {copied ? <CheckIcon sx={{ fontSize: 12 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
    </button>
  );
};

const RoleBadge = ({ role }: { role: Role }) => (
  <span className="monitor-role-badge" style={{ backgroundColor: ROLE_COLORS[role] }}>
    {role}
  </span>
);

const PartyCard = ({
  role,
  member,
  fallbackName,
  idValue,
}: {
  role: Role;
  member?: any;
  fallbackName: string;
  idValue: string;
}) => {
  const color = ROLE_COLORS[role];
  const name = member?.user?.name || fallbackName;
  return (
    <div className="monitor-party" style={{ ['--role-color' as any]: color }}>
      <Avatar
        className="monitor-party-avatar"
        src={member?.user?.image}
        sx={{ width: 38, height: 38, bgcolor: color, fontSize: '0.9rem' }}
      >
        {(name || '?').charAt(0).toUpperCase()}
      </Avatar>
      <div className="monitor-party-info">
        <div className="monitor-party-top">
          <RoleBadge role={role} />
          <span className="monitor-party-name">{name || '\u2014'}</span>
        </div>
        <IdChip value={idValue} />
      </div>
    </div>
  );
};

const MonitorChannelView = () => {
  const { channel, messages } = useChannelStateContext();

  const members = useMemo(() => Object.values(channel?.state?.members ?? {}), [channel]);
  const gigTitle = (channel?.data as any)?.ggData?.gigTitle as string | undefined;
  const { buyerId, gigId } = useMemo(() => parseChannelId(channel?.id), [channel?.id]);

  const roleFor = (userId?: string, memberRole?: string): Role => {
    if (memberRole === 'admin') return 'Admin';
    if (!userId) return 'Unknown';
    if (userId === buyerId) return 'Buyer';
    return 'Seller';
  };

  const buyerMember = members.find((m: any) => m.user?.id === buyerId) as any;
  const sellerMember = members.find(
    (m: any) => m.user?.role !== 'admin' && m.user?.id !== buyerId,
  ) as any;

  return (
    <div className="monitor-view">
      <div className="monitor-view-header">
        <div className="monitor-head-top">
          <div className="monitor-head-titlewrap">
            <span className="monitor-view-eye">&#128065;</span>
            <span className="monitor-head-title">{gigTitle || 'Untitled conversation'}</span>
          </div>
          <div className="monitor-head-right">
            <IdChip label="Gig" value={gigId} />
            <span className="monitor-head-count">{messages?.length ?? 0} msgs</span>
          </div>
        </div>

        <div className="monitor-head-parties">
          <PartyCard
            role="Buyer"
            member={buyerMember}
            fallbackName={buyerId}
            idValue={buyerId}
          />
          <span className="monitor-party-arrow">&#8594;</span>
          <PartyCard
            role="Seller"
            member={sellerMember}
            fallbackName="Unknown seller"
            idValue={sellerMember?.user?.id ?? ''}
          />
        </div>
      </div>

      <div className="monitor-view-messages">
        {(!messages || messages.length === 0) && (
          <div className="monitor-view-empty">No messages in this channel.</div>
        )}

        {messages?.map((message: any) => {
          const userId = message.user?.id ?? 'unknown';
          const userName = message.user?.name || userId;
          const role = roleFor(message.user?.id, message.user?.role);
          const color = ROLE_COLORS[role];
          const isDeleted = message.type === 'deleted';
          const customData = message.ggCustomMessageData;
          const text = isDeleted ? 'This message was deleted.' : message.text;

          return (
            <div className="monitor-msg" key={message.id} style={{ borderLeftColor: color }}>
              <Avatar
                className="monitor-msg-avatar"
                alt={userName}
                src={message.user?.image}
                sx={{ width: 32, height: 32, bgcolor: color, fontSize: '0.8rem' }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <div className="monitor-msg-body">
                <div className="monitor-msg-meta">
                  <RoleBadge role={role} />
                  <span className="monitor-msg-sender" style={{ color }}>{userName}</span>
                  <span className="monitor-msg-id">{userId}</span>
                  <span className="monitor-msg-time">{formatTime(message.created_at)}</span>
                </div>
                {customData && !isDeleted ? (
                  <div className="monitor-msg-custom">
                    <span className="monitor-msg-custom-tag">{customData.type}</span>
                    <GGCustomMessage customMessageDetails={customData} channelId={channel?.id ?? ''} />
                  </div>
                ) : text ? (
                  <div className={`monitor-msg-text${isDeleted ? ' monitor-msg-deleted' : ''}`}>{text}</div>
                ) : (
                  <div className="monitor-msg-text monitor-msg-nontext">
                    [{message.type || 'non-text'} message
                    {message.attachments?.length ? ` · ${message.attachments.length} attachment(s)` : ''}]
                  </div>
                )}
                {message.attachments?.map((att: any, idx: number) => (
                  att.image_url || att.thumb_url ? (
                    <img
                      key={idx}
                      className="monitor-msg-attachment"
                      src={att.image_url || att.thumb_url}
                      alt={att.fallback || 'attachment'}
                    />
                  ) : null
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonitorChannelView;
